import { Box, Collapse, Dialog, Tab, Tabs } from '@mui/material';
import { Link as RouterLink, useNavigate } from '@tanstack/react-router';
import React, { useCallback, useState } from 'react';

import EmailLoginDialog from '@/features/identity/components/EmailLoginDialog';
import EmailResetForm from '@/features/identity/components/EmailResetForm';
import EmailSSOLoginForm from '@/features/identity/components/EmailSSOLoginForm';
import { EmailIcon } from '@/icons/EmailIcon';
import { EmailIconColored } from '@/icons/EmailIconColored';
import { SsoIcon } from '@/icons/SsoIcon';
import WeavrLogo from '@/public/svgs/new_logo_transparent.svg';

interface IProps {
  open: boolean;
  setOpen: (b: boolean) => void;
  resetUserId: string | undefined;
  resetToken: string | undefined;
}

const LoginDialog = ({ open, setOpen, resetUserId, resetToken }: IProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const isReset = !!resetUserId && !!resetToken;

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  }, []);

  const handleResetSuccess = useCallback(() => {
    navigate({ to: '/' });
  }, [navigate]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        setOpen(false);
      }}
      sx={{
        '& .MuiDialog-paper': {
          m: 0,
          width: '100%',
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100vh',
          backgroundColor: '#FCFCF9',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }
      }}
    >
      <div className='flex flex-col items-center gap-6'>
        <Box className='flex items-center gap-2'>
          <img src={WeavrLogo} alt='Weavr Logo' />
          <h4 className='text-3xl font-semibold text-[#041925]'>weavr.</h4>
        </Box>

        <Collapse in={!isReset} className='w-full max-w-[400px] rounded-[12px] border border-[#E6E6E6] bg-white p-6'>
          <div className='space-y-4'>
            <h1 className='text-center text-xl font-semibold text-[#111111]'>Sign In</h1>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label='login options'
              TabIndicatorProps={{ sx: { height: '4px', backgroundColor: '#9C5738' } }}
              sx={{
                minHeight: '50px',
                '& .MuiTab-root': {
                  minHeight: '20px',
                  textTransform: 'none',
                  color: '#9A9A9A',
                  fontSize: '15px',
                  '&.Mui-selected': {
                    color: '#000000'
                  }
                }
              }}
            >
              <Tab
                id='login-tab-0'
                label='Single Sign-On'
                icon={<SsoIcon />}
                iconPosition='start'
                aria-controls='login-tabpanel-0'
              />
              <Tab
                id='login-tab-1'
                label='Email & Password'
                icon={activeTab === 1 ? <EmailIconColored /> : <EmailIcon />}
                iconPosition='start'
                aria-controls='login-tabpanel-1'
              />
            </Tabs>
            <div className='pt-1'>
              <Collapse in={activeTab === 0} timeout='auto' unmountOnExit>
                <div role='tabpanel' id='login-tabpanel-0' aria-labelledby='login-tab-0'>
                  <EmailSSOLoginForm />
                </div>
              </Collapse>
              <Collapse in={activeTab === 1} timeout='auto' unmountOnExit>
                <div role='tabpanel' id='login-tabpanel-1' aria-labelledby='login-tab-1'>
                  <EmailLoginDialog />
                </div>
              </Collapse>
            </div>
          </div>
        </Collapse>
        {isReset && (
          <Collapse in={isReset} className='w-full max-w-[384px] rounded-[12px] border border-[#E6E6E6] bg-white p-6'>
            <EmailResetForm resetToken={resetToken!} resetUserId={resetUserId!} onResetSuccess={handleResetSuccess} />
          </Collapse>
        )}
        {!isReset && (
          <p className='max-w-[229px] text-center text-xs text-[#737373]'>
            By clicking continue, you agree to our{' '}
            <RouterLink to='/terms' className='text-[#777777] underline'>
              Terms of Service
            </RouterLink>{' '}
            and{' '}
            <RouterLink to='/privacy_policy' className='text-[#777777] underline'>
              Privacy Policy
            </RouterLink>
            .
          </p>
        )}
      </div>
    </Dialog>
  );
};

export default LoginDialog;
